import React from 'react'
import propTypes from 'prop-types'
import withImmutablePropsToJS from 'with-immutable-props-to-js'

import { Button, ButtonGroup, Tooltip, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'

import { Content, Header, HeaderBreadCrum, Layout } from '../../layouts/AuthenticatedLayout'
import RenderContentIndex from '../../common/renderContentIndex/RenderContentIndex'
import SearchBox from '../../common/SearchToolbox'
import Pagination from '../../common/Pagination/Pagination'
import Authorization from '../../common/AuthorizationEvaluation'
import { PORTAL_ADMINISTRATOR } from '../../../resources/RolesEnum'
import { useDataGridToolbarStyle } from '../../../resources/styles/CommonIndexStyles'
import headerConfig from '../resources/substituteMaterialsConfig'

export const EMPTY_CATALOG_MESSAGE = 'There are no substitute materials registered yet.'
export const NO_SEARCH_RESULTS_MESSAGE = 'No substitute materials match your search.'

/**
 * Message shown in place of the listing when it has no rows.
 *
 * An empty catalog and a search that matched nothing look the same in the data but mean opposite
 * things to the user: one invites registering the first record, the other invites correcting the
 * filter. They are told apart by whether a search criteria is applied.
 * @param {*} searchValue
 */
export const getEmptyMessage = (searchValue) => {
	let hasSearch = !!searchValue && String(searchValue).trim().length > 0
	return hasSearch ? NO_SEARCH_RESULTS_MESSAGE : EMPTY_CATALOG_MESSAGE
}

const DesktopView = (props) => {
	const classes = useDataGridToolbarStyle()
	const {
		items,
		pagination,
		isLoading,
		isSaving,
		searchValue,
		onChangeSearchValue,
		onChangeSortingCriteria,
		onChangePage,
		onEditClick,
		onRemoveClick,
		userProfile
	} = props

	/**
	 * Per-row maintenance actions, appended to the catalog columns rather than declared with them:
	 * the column configuration describes the catalog, these describe what can be done to a row.
	 */
	const getHeaderConfiguration = () => {
		if (!onEditClick && !onRemoveClick) return headerConfig
		return [
			...headerConfig,
			{
				title: '',
				dataSource: 'options',
				onRenderProperty: (item) => (
					<ButtonGroup component='span' size='small' variant='text'>
						<Tooltip component='span' title='Edit substitute material'>
							<Button onClick={() => onEditClick(item)} disabled={isSaving}>
								<EditIcon fontSize='small' />
							</Button>
						</Tooltip>
						<Tooltip component='span' title='Remove substitute material'>
							<Button onClick={() => onRemoveClick(item)} disabled={isSaving}>
								<DeleteIcon fontSize='small' />
							</Button>
						</Tooltip>
					</ButtonGroup>
				)
			}
		]
	}

	return (
		<Layout>
			<Header
				key={'AuthenticatedHeader'}
				user={userProfile}
				breadcrumbs={
					<HeaderBreadCrum>
						<Typography color='secondary'>Administration / Substitute Materials</Typography>
					</HeaderBreadCrum>
				}
			/>
			<Content key={'AuthenticatedContent'}>
				<div className={classes.rootIndex}>
					{/* The toolbar renders regardless of whether the listing has rows, so the actions
					    it hosts stay reachable when the catalog is empty. */}
					<DataGridToolbar
						searchValue={searchValue}
						onChange={onChangeSearchValue}
						onAddClick={props.onAddClick}
						onImportClick={props.onImportClick}
						userProfile={userProfile}
						isSaving={isSaving}
					/>
					<RenderContentIndex
						headerConfig={getHeaderConfiguration()}
						isLoading={isLoading}
						pagination={pagination}
						data={items}
						gridMode={false}
						onChangeSortingCriteria={onChangeSortingCriteria}
						searchValue={searchValue}
						titleData={getEmptyMessage(searchValue)}
						isAvatarFromIdentity={false}
						isActiveButtonOptions={false}
					/>
					<div className={classes.pagination}>
						<div className={classes.flexGrow} />
						<Pagination
							count={pagination ? pagination.total : 0}
							page={pagination ? pagination.pageNumber : 0}
							onChangePage={onChangePage}
						/>
					</div>
				</div>
			</Content>
		</Layout>
	)
}

/**
 * Search box plus the catalog-level actions.
 *
 * Mirrors the toolbar of the Series under warranty SAP Filters view — same `searchIndex` wrapper,
 * same `ButtonGroup` with `buttonsGroupContainer`, same `buttonsGroup` on each button, and the same
 * `Authorization` wrapper around the mutating actions — so both administration screens read as one
 * family instead of two variations.
 */
const DataGridToolbar = (props) => {
	const classes = useDataGridToolbarStyle()

	return (
		<div className={classes.searchIndex}>
			<SearchBox placeholder='Search substitute material' onChange={props.onChange} value={props.searchValue} />
			<ButtonGroup component='span' size='small' variant='outlined' color='primary' className={classes.buttonsGroupContainer}>
				<Authorization profile={props.userProfile} allowedRoles={[PORTAL_ADMINISTRATOR]}>
					<Tooltip component='span' title='Add substitute material'>
						<Button className={classes.buttonsGroup} onClick={props.onAddClick} disabled={props.isSaving}>
							<AddIcon />
						</Button>
					</Tooltip>
				</Authorization>
				<Authorization profile={props.userProfile} allowedRoles={[PORTAL_ADMINISTRATOR]}>
					<Tooltip component='span' title='Import substitute materials'>
						<Button className={classes.buttonsGroup} onClick={props.onImportClick} disabled={props.isSaving}>
							<PublishIcon />
						</Button>
					</Tooltip>
				</Authorization>
			</ButtonGroup>
		</div>
	)
}

DataGridToolbar.propTypes = {
	/**On change search value */
	onChange: propTypes.func.isRequired,
	/**Initial search value */
	searchValue: propTypes.string,
	/**Opens the panel to register a new relation */
	onAddClick: propTypes.func,
	/**Opens the dialog to import the catalog from a file */
	onImportClick: propTypes.func,
	/**Logged user, used to gate the mutating actions */
	userProfile: propTypes.object,
	/**Whether a mutation is in flight */
	isSaving: propTypes.bool
}

DataGridToolbar.defaultProps = {
	searchValue: '',
	onAddClick: null,
	onImportClick: null,
	userProfile: null,
	isSaving: false,
	onChange: () => console.warn('Callback [onChange] no defined')
}

DesktopView.propTypes = {
	/**Rows of the catalog page currently loaded */
	items: propTypes.array.isRequired,
	/**Pagination reported by the server */
	pagination: propTypes.object,
	/**Whether the catalog query is in flight */
	isLoading: propTypes.bool.isRequired,
	/**Search criteria currently applied */
	searchValue: propTypes.string,
	/**Callback invoked when the search criteria changes */
	onChangeSearchValue: propTypes.func.isRequired,
	/**Callback invoked when the sorting criteria changes */
	onChangeSortingCriteria: propTypes.func.isRequired,
	/**Callback invoked when the page changes */
	onChangePage: propTypes.func.isRequired,
	/**Opens the panel to register a new relation */
	onAddClick: propTypes.func,
	/**Opens the dialog to import the catalog from a file */
	onImportClick: propTypes.func,
	/**Whether a maintenance operation is in flight */
	isSaving: propTypes.bool,
	/**Opens the panel to edit the given row */
	onEditClick: propTypes.func,
	/**Opens the confirmation to remove the given row */
	onRemoveClick: propTypes.func
}

DesktopView.defaultProps = {
	items: [],
	pagination: null,
	isLoading: false,
	isSaving: false,
	searchValue: '',
	onAddClick: null,
	onImportClick: null,
	onEditClick: null,
	onRemoveClick: null,
	onChangeSearchValue: () => console.warn('Callback [onChangeSearchValue] no defined'),
	onChangeSortingCriteria: () => console.warn('Callback [onChangeSortingCriteria] no defined'),
	onChangePage: () => console.warn('Callback [onChangePage] no defined')
}

export default withImmutablePropsToJS(DesktopView)
